import { useState } from 'react';
import {
  Reply,
  Forward,
  Mail,
  MailOpen,
  Tag,
  Trash2,
  ArrowDownLeft,
  ArrowUpRight,
} from 'lucide-react';
import { Sheet } from '@/components/ui/Sheet';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { EmailClassificationBadge } from '@/components/modules/EmailClassificationBadge';
import { AutonomyIndicator } from '@/components/modules/AutonomyIndicator';
import { SentimentIndicator } from '@/components/modules/SentimentIndicator';
import { formatDateTime } from '@/lib/format';
import { useEmailStore } from '@/stores/emailStore';
import type { EmailMessage } from '@nexus/shared/types';

export interface ComposePreFill {
  to: string;
  subject: string;
  body: string;
}

interface EmailSheetProps {
  id: string | null;
  open: boolean;
  onClose: () => void;
  onCompose: (preFill: ComposePreFill) => void;
}

export function EmailSheet({ id, open, onClose, onCompose }: EmailSheetProps) {
  const { selected, setSelected, markRead, deleteMessage, classify } = useEmailStore();
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [classifying, setClassifying] = useState(false);

  // Sync selected when id changes
  // setSelected is called from the parent (EmailOps) on row click

  const email: EmailMessage | null = selected;

  if (!email) {
    return (
      <Sheet open={open} onClose={onClose} title="Email">
        <div className="flex items-center justify-center py-12 text-surface-500">
          Loading...
        </div>
      </Sheet>
    );
  }

  const handleReply = () => {
    onCompose({
      to: email.from,
      subject: email.subject.startsWith('Re:') ? email.subject : `Re: ${email.subject}`,
      body: `\n\n--- Original Message ---\nFrom: ${email.from}\nDate: ${formatDateTime(email.createdAt)}\nSubject: ${email.subject}\n\n${email.bodyPreview}`,
    });
    onClose();
  };

  const handleForward = () => {
    onCompose({
      to: '',
      subject: email.subject.startsWith('Fwd:') ? email.subject : `Fwd: ${email.subject}`,
      body: `\n\n--- Forwarded ---\nFrom: ${email.from}\nTo: ${email.to.join(', ')}\nDate: ${formatDateTime(email.createdAt)}\nSubject: ${email.subject}\n\n${email.bodyPreview}`,
    });
    onClose();
  };

  const handleToggleRead = async () => {
    await markRead(email.id, !email.isRead);
  };

  const handleClassify = async () => {
    setClassifying(true);
    try {
      await classify(email.id);
    } finally {
      setClassifying(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteMessage(email.id);
      setConfirmDeleteOpen(false);
      onClose();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Sheet open={open} onClose={onClose} title="Email Details" width="xl">
        <div className="space-y-6">
          {/* Header: Subject + Badges */}
          <div className="space-y-3">
            <h3 className="text-lg font-bold text-surface-100 leading-tight">
              {email.subject}
            </h3>
            <div className="flex items-center flex-wrap gap-2">
              <EmailClassificationBadge classification={email.classification} />
              <Badge variant={email.direction === 'inbound' ? 'info' : 'success'}>
                <span className="flex items-center gap-1">
                  {email.direction === 'inbound' ? (
                    <ArrowDownLeft className="h-3 w-3" />
                  ) : (
                    <ArrowUpRight className="h-3 w-3" />
                  )}
                  {email.direction === 'inbound' ? 'Inbound' : 'Outbound'}
                </span>
              </Badge>
              {!email.isRead && (
                <Badge variant="warning">Unread</Badge>
              )}
              {email.autonomyAction && (
                <AutonomyIndicator level={2} action={email.autonomyAction} />
              )}
              {email.sentiment && (
                <SentimentIndicator sentiment={email.sentiment} showLabel />
              )}
            </div>
          </div>

          {/* Metadata bar */}
          <div className="bg-surface-900/50 border border-surface-700/50 rounded-lg px-4 py-3 space-y-2">
            <div className="grid grid-cols-[60px_1fr] gap-y-1.5 items-baseline">
              <span className="text-xs text-surface-500 font-medium">From</span>
              <span className="text-sm text-surface-200">{email.from}</span>

              <span className="text-xs text-surface-500 font-medium">To</span>
              <span className="text-sm text-surface-200">{email.to.join(', ')}</span>

              {email.cc && email.cc.length > 0 && (
                <>
                  <span className="text-xs text-surface-500 font-medium">CC</span>
                  <span className="text-sm text-surface-200">{email.cc.join(', ')}</span>
                </>
              )}

              <span className="text-xs text-surface-500 font-medium">Date</span>
              <span className="text-sm text-surface-300">{formatDateTime(email.createdAt)}</span>
            </div>
          </div>

          {/* Body */}
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-surface-500 uppercase tracking-wider">
              Message
            </h4>
            <div className="bg-surface-900/30 border border-surface-700/50 rounded-lg px-4 py-4">
              <p className="text-sm text-surface-200 leading-relaxed whitespace-pre-wrap">
                {email.bodyPreview || 'No content available.'}
              </p>
            </div>
          </div>

          {/* Actions row */}
          <div className="flex items-center flex-wrap gap-2 pt-2 border-t border-surface-700">
            <Button
              variant="secondary"
              size="sm"
              icon={<Reply className="h-3.5 w-3.5" />}
              onClick={handleReply}
            >
              Reply
            </Button>
            <Button
              variant="secondary"
              size="sm"
              icon={<Forward className="h-3.5 w-3.5" />}
              onClick={handleForward}
            >
              Forward
            </Button>
            <Button
              variant="ghost"
              size="sm"
              icon={email.isRead ? <Mail className="h-3.5 w-3.5" /> : <MailOpen className="h-3.5 w-3.5" />}
              onClick={handleToggleRead}
            >
              {email.isRead ? 'Mark Unread' : 'Mark Read'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              icon={<Tag className="h-3.5 w-3.5" />}
              onClick={handleClassify}
              loading={classifying}
            >
              Classify
            </Button>
            <div className="ml-auto">
              <Button
                variant="danger"
                size="sm"
                icon={<Trash2 className="h-3.5 w-3.5" />}
                onClick={() => setConfirmDeleteOpen(true)}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      </Sheet>

      <ConfirmDialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={handleDelete}
        title="Delete Email"
        description="Are you sure you want to delete this email? This action cannot be undone."
        confirmLabel="Delete"
        loading={deleting}
      />
    </>
  );
}
